<template>
  <ApplicationDisconnectDialog
    v-if="props.application"
    v-model:visibility="applicationDisconnectDialogVisible"
    :application="props.application" />

  <el-card
    shadow="always"
    :body-style="{
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      padding: '30px 20px',
      alignItems: 'center',
      gap: '25px',
    }">
    <!-- Application Info -->
    <div class="stats-card-application-info-container items-center gap-6">
      <!-- Logo -->
      <ApplicationIcon
        v-if="internalName"
        :application-name="internalName"
        :size="50"
        :application-type="applicationType" />
      <SmSkeleton v-else width="50px" height="50px" />

      <!-- Name, Edit Button and Description -->
      <div class="flex flex-col justify-center gap-2">
        <div class="title flex items-center gap-3">
          <TextMultiline
            v-if="currentObject?.display_name || application?.software_name"
            :text="currentObject?.display_name || application?.software_name"
            style="
              font-size: 1.2rem;
              font-weight: 600;
              width: minmax(0, max-content);
            " />
          <SmSkeleton v-else width="40%" />

          <SmTooltip v-if="application?.read_only">
            <template #content> {{ i18n.t('readonlyDesc') }} </template>
            <span class="no-data">({{ i18n.t('readonlyShort') }})</span>
          </SmTooltip>

          <SmDropdown v-if="!isSSO" type="panel" placement="right">
            <template #trigger>
              <TextMultiline :show-tool-tip="false">
                <div class="item-center group/button flex w-full gap-1">
                  <SquarePen
                    :size="16"
                    class="mb-[2px] stroke-contrast-muted hover:stroke-contrast"
                    style="cursor: pointer" />
                </div>
              </TextMultiline>
            </template>
            <SmDropdownItem @click="handleApplicationEditButton">
              {{ i18n.t('editApplication') }}
            </SmDropdownItem>
            <SmDropdownItem @click="handleApplicationDeleteButton">
              {{ i18n.t('views.applications.table.deleteApplication') }}
            </SmDropdownItem>
          </SmDropdown>
        </div>
        <SmSkeleton v-if="!currentObject" width="70%" />
        <TextMultiline
          v-else-if="currentObject?.desc_short"
          :text="currentObject?.desc_short"
          :lines-to-show="2"
          :show-tool-tip="false" />

        <ApplicationOwnerTag
          :owner-account-ids="props.application?.owner_account_ids" />
      </div>
    </div>

    <!-- Stats -->
    <RouterLink
      v-if="application"
      :to="`/applications/${application._id}/${subAccountID}/accounts`">
      <StatsElement
        :display-number="userCount"
        display-i18n-key="views.applicationDetails.statsCard.people.title"
        :icon="User"
        color="orange" />
    </RouterLink>
    <RouterLink
      v-if="application"
      :to="`/applications/${application._id}/${subAccountID}/optimizations`">
      <StatsElement
        :display-number="optimizationCount"
        display-i18n-key="views.applicationDetails.statsCard.optimizations.title"
        :icon="ChartLine"
        color="purple" />
    </RouterLink>
    <RouterLink
      v-if="application"
      :to="`/applications/${application._id}/${subAccountID}/licenses`">
      <StatsElement
        :display-number="monthlyCost"
        is-price
        display-i18n-key="views.applicationDetails.statsCard.spends.title"
        :icon="Wallet"
        color="blue" />
    </RouterLink>
  </el-card>
</template>

<script lang="ts" setup>
  import {
    SoftwareCostService,
    SoftwareOut,
    UserLicencesService,
  } from '@/client'
  import { OptimizationStore } from '@/stores/optimization/optimization.store'
  import { usePreferenceStore } from '@/stores/preferenceStore'
  import { useContractStore } from '@/stores/contractStore'
  import { useI18n } from 'vue-i18n'
  import { useRoute, RouterLink } from 'vue-router'
  import { User, SquarePen, ChartLine, Wallet } from 'lucide-vue-next'

  const i18n = useI18n()
  const preferenceStore = usePreferenceStore()

  const props = defineProps({
    application: {
      type: Object as PropType<SoftwareOut>,
      default: null,
    },
  })

  const emit = defineEmits<{
    openApplicationDetailsDrawer: [value: string]
  }>()

  const route = useRoute()

  const subAccountID = computed(() =>
    decodeURIComponent(route.params.subAccountId as string)
  )
  const isSSO = computed(() => route.query.sso === 'true')

  const internalName = computed(() => {
    if (!props.application) return

    if (isSSO.value) {
      return props.application.sub_accounts[subAccountID.value].name
    } else if (props.application?.software_name) {
      return props.application?.software_name
    }
  })

  const applicationType = computed(() => {
    if (!props.application) return

    if (isSSO.value) {
      return 'SSO'
    } else {
      return props.application.type
    }
  })

  const currentObject = computed(() => {
    if (!props.application) return

    if (isSSO.value) {
      return props.application.sub_accounts[subAccountID.value]
    } else {
      return props.application
    }
  })

  const applicationDisconnectDialogVisible = ref(false)
  const handleApplicationEditButton = () => {
    emit('openApplicationDetailsDrawer', 'EditSoftware')
  }

  const handleApplicationDeleteButton = () => {
    applicationDisconnectDialogVisible.value = true
  }

  const optimizationCount = ref()
  const monthlyCost = ref()
  const userCount = ref<number>()
  async function fetchStatistics(application_id: string) {
    OptimizationStore.getOptimizationsCount({
      application_ids: [application_id],
      hidden: false,
      deprecated: false,
    }).then((res) => {
      optimizationCount.value = res
    })

    const query: Record<string, string> = {
      id: application_id,
    }

    if (isSSO.value) {
      query['subAccountId'] = subAccountID.value
    }

    const queryLicences: Record<string, string> = {
      softwareId: application_id,
    }

    if (isSSO.value) {
      queryLicences['subAccountId'] = subAccountID.value
    }

    UserLicencesService.countUniqueUserLicencesApiV1SoftwareLicencesCountUniqueUserLicencesGet(
      queryLicences
    ).then((res) => {
      userCount.value = res
    })

    await preferenceStore.getPreferences()
    SoftwareCostService.getCostsApiV1SoftwareCostGet({
      ...query,
      targetCurrencyCode: preferenceStore.preferences?.currency_code,
    }).then((res) => {
      if (isSSO.value) {
        monthlyCost.value =
          res.software_costs[application_id].sub_account_costs[
            subAccountID.value
          ]
      } else {
        monthlyCost.value = res.total || 0
      }
    })
  }

  watch(
    () => props.application,
    (newVal, oldVal) => {
      if (props.application === null) return
      if (JSON.stringify(newVal) === JSON.stringify(oldVal)) return
      fetchStatistics(props.application._id)
    },
    { immediate: true }
  )

  // Reload stats if contracts change
  const contractStore = useContractStore()
  const changeLog = computed(() => contractStore.lastChangedTime)

  watch(
    () => changeLog,
    () => {
      fetchStatistics(props.application._id)
    },
    { deep: true }
  )

  defineExpose({
    fetchStatistics,
  })
</script>

<style lang="scss" scoped>
  .stats-card-application-info-container {
    display: grid;
    place-content: center;

    grid-template-columns: 50px 1fr;
    flex: 1;
    max-width: 40%;
    .company-img {
      height: 100%;
      width: 100%;

      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
  .edit-button-content {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .edit-button {
    .edit-icon {
      fill: var(--sm-petrol);
    }

    &:hover {
      .edit-icon {
        fill: var(--sm-white);
      }
    }
  }
</style>
